import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';
import { StatusPill } from '../components/StatusPill.js';
import { VerdictPill } from '../components/VerdictPill.js';
import type { Run } from '../lib/types.js';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

export function RunDetail() {
  const { id = '' } = useParams();
  const query = useApiQuery<Run>(['run', id], `/api/runs/${encodeURIComponent(id)}`);

  if (query.isLoading) return <p>Loading run…</p>;
  if (query.isError || !query.data) return <p className="error">Failed to load run.</p>;

  const run = query.data;
  const te = run.test_env;

  return (
    <section>
      <div className="detail-head">
        <h2>Run {run.run_id}</h2>
        <Link className="btn-link" to={`/runs/${encodeURIComponent(run.run_id)}/logs`}>View logs →</Link>
      </div>

      <dl className="def-list">
        <Row label="Status"><StatusPill status={run.status} /></Row>
        <Row label="Repo">{run.repo ?? '—'}</Row>
        <Row label="User">{run.user_id ?? '—'}</Row>
        <Row label="Model">{run.model ?? '—'}</Row>
        <Row label="Turns">
          {run.num_turns != null && run.max_turns != null
            ? `${run.num_turns}/${run.max_turns}`
            : run.num_turns != null ? String(run.num_turns) : '—'}
        </Row>
        <Row label="Created">{run.created_at ?? '—'}</Row>
        <Row label="Finished">{run.finished_at ?? '—'}</Row>
        <Row label="Container image">{run.container_image ?? '—'}</Row>
        <Row label="Sidecars">
          {run.sidecars && run.sidecars.length > 0 ? run.sidecars.join(', ') : '—'}
        </Row>
        <Row label="Task ARN">{run.task_arn ?? '—'}</Row>
        <Row label="PR">
          {run.pr_url
            ? <a href={run.pr_url} target="_blank" rel="noreferrer">{run.pr_url}</a>
            : '—'}
        </Row>
        {run.error && <Row label="Error"><span className="error-text">{run.error}</span></Row>}
      </dl>

      <h3>Test environment</h3>
      {te ? (
        <dl className="def-list">
          <Row label="Verdict"><VerdictPill verdict={te.verdict ?? undefined} /></Row>
          <Row label="Deps">{te.deps ?? '—'}</Row>
          <Row label="Test DB">{te.test_db ?? '—'}</Row>
          <Row label="Detail">{te.detail ?? '—'}</Row>
        </dl>
      ) : (
        <p className="empty">No test-environment data.</p>
      )}
    </section>
  );
}
